import {
  Box,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ReportMetrics, pct } from "./reportMetrics";

// 테마 색이 클래스 수보다 적으면 이어서 쓰는 고정 순서 팔레트(순환 금지)
const FALLBACK = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6", "#8a8a85"];
const AXIS = { fontSize: 12, fill: "var(--text-secondary-color, #52514e)" };
const GRID = "rgba(128,128,128,0.2)";
const WARN = "#d03b3b";

interface ContentProps {
  m: ReportMetrics;
  colors: string[];
  // 편집 가능 콘텐츠(분석 소견)용
  editedText?: string; // 사용자가 수정한 내용, "" = 자동 생성 내용 사용
  onEditedTextChange?: (text: string) => void;
  editing?: boolean;
  onEditEnd?: () => void;
}

// 클래스가 아닌 시리즈(라벨/AI, 과검/미검)도 차트 테마 색을 순서대로 사용
const series = (colors: string[], i: number) => colors[i] ?? FALLBACK[i];

const classColor = (m: ReportMetrics, colors: string[], c: string) => {
  const i = m.classes.indexOf(c);
  return colors[i] ?? FALLBACK[i % FALLBACK.length];
};

const Empty = ({ children = "표시할 데이터가 없습니다." }: { children?: ReactNode }) => (
  <Typography sx={{ py: 4, textAlign: "center", color: "var(--text-secondary-color)" }}>{children}</Typography>
);

const cellSx = { py: 0.75, px: 1, fontSize: 13, color: "var(--text-color)" };
const headSx = {
  ...cellSx,
  fontWeight: 700,
  color: "var(--text-secondary-color)",
};

// 요약 KPI
function Summary({ m }: ContentProps) {
  const r = m.latestRound;
  const tiles: { label: string; value: string; sub: string; warn?: boolean }[] = m.isInspection
    ? [
        // 검사: 검증 리스트 기준 (사용자 라벨은 항상 채워져 있어 라벨링률은 의미 없음)
        {
          label: "검증 완료",
          value: m.total.toLocaleString(),
          sub: `정답 라벨 매칭 ${m.labeled.toLocaleString()}장`,
        },
        {
          label: "AI 판정 NG",
          value: m.classStats.reduce((s, c) => s + c.predicted, 0).toLocaleString(),
          sub: `정답 NG ${m.ngLabeled.toLocaleString()}장`,
        },
        {
          label: "라벨-AI 일치율",
          value: pct(m.matchRate),
          sub: `AI 판정 ${m.aiJudged.toLocaleString()}장 기준`,
        },
        {
          label: "과검률",
          value: pct(m.overkillRate),
          sub: "정답 OK → AI NG",
          warn: (m.overkillRate ?? 0) > 0.05,
        },
        {
          label: "미검률",
          value: pct(m.missingRate),
          sub: "정답 NG → AI OK",
          warn: (m.missingRate ?? 0) > 0.05,
        },
        {
          label: "임계값",
          value: m.validation?.threshold?.toFixed(4) ?? "-",
          sub: "score > 임계값 → NG",
        },
      ]
    : [
        {
          label: "전체 이미지",
          value: m.total.toLocaleString(),
          sub: `미라벨 ${m.unlabeled.toLocaleString()}장`,
        },
        {
          label: "라벨링 완료",
          value: m.labeled.toLocaleString(),
          sub: `라벨링률 ${pct(m.total ? m.labeled / m.total : null)}`,
        },
        {
          label: "NG 라벨",
          value: m.ngLabeled.toLocaleString(),
          sub: `NG 비율 ${pct(m.labeled ? m.ngLabeled / m.labeled : null)}`,
        },
        {
          label: "라벨-AI 일치율",
          value: pct(m.matchRate),
          sub: `AI 판정 ${m.aiJudged.toLocaleString()}장 기준`,
        },
        {
          label: "과검률",
          value: pct(r?.overkill),
          sub: r ? `${r.iteration}회차 모델` : "학습 결과 없음",
          warn: (r?.overkill ?? 0) > 0.05,
        },
        {
          label: "미검률",
          value: pct(r?.missing),
          sub: r ? `임계값 ${r.threshold ?? "-"}` : "학습 결과 없음",
          warn: (r?.missing ?? 0) > 0.05,
        },
      ];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: 1.5,
      }}
    >
      {tiles.map((t) => (
        <Box
          key={t.label}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: "1px solid var(--border-color, #e0e0e0)",
          }}
        >
          <Typography sx={{ fontSize: 13, color: "var(--text-secondary-color)" }}>{t.label}</Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: "var(--text-color)" }}>
            {t.value}
            {t.warn && <span style={{ fontSize: 13, color: WARN, marginLeft: 6 }}>▲ 기준 5% 초과</span>}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "var(--text-secondary-color)" }}>{t.sub}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// 자동 분석 소견 (보고서 서술용)
function Findings({ m, editedText, onEditedTextChange, editing, onEditEnd }: ContentProps) {
  const lines: string[] = [];
  if (m.isInspection && m.validation)
    lines.push(
      `검증 #${m.validation.id}(모델 ${m.validation.modelName}, 임계값 ${m.validation.threshold?.toFixed(4) ?? "-"}) 기준 검증 이미지 ${m.total}장, 과검률 ${pct(m.overkillRate)}, 미검률 ${pct(m.missingRate)}입니다.`,
    );
  const weakClass = [...m.classStats].filter((s) => s.recall !== null).sort((a, b) => a.recall! - b.recall!)[0];
  if (weakClass)
    lines.push(
      `정확도가 가장 낮은 클래스는 '${weakClass.className}'(${pct(weakClass.recall)}, 라벨 ${weakClass.labeled}장)입니다. 추가 라벨링/데이터 보강을 권장합니다.`,
    );
  const bigClass = [...m.classStats].sort((a, b) => b.labeled - a.labeled)[0];
  if (bigClass?.labeled) lines.push(`가장 많이 발생한 불량은 '${bigClass.className}'(${bigClass.labeled}장)입니다.`);
  const worstProc = [...m.processStats]
    .filter((p) => p.mismatchRate !== null)
    .sort((a, b) => b.mismatchRate! - a.mismatchRate!)[0];
  if (worstProc) {
    const top = Object.entries(worstProc.byClass).sort((a, b) => b[1] - a[1])[0];
    lines.push(
      `'${worstProc.process}' 공정의 오판정률이 ${pct(worstProc.mismatchRate)}로 가장 높으며${top ? `, 주요 불량은 '${top[0]}'(${top[1]}장)` : ""}입니다.`,
    );
  }
  const done = m.rounds.filter((r) => r.overkill !== null);
  if (done.length >= 2) {
    const [first, last] = [done[0], done[done.length - 1]];
    lines.push(
      `학습 ${first.iteration}→${last.iteration}회차 동안 과검률 ${pct(first.overkill)} → ${pct(last.overkill)}, 미검률 ${pct(first.missing)} → ${pct(last.missing)}로 변화했습니다.`,
    );
  }
  const failed = m.rounds.filter((r) => r.status === "Failed" || r.status === "Stop").length;
  if (failed) lines.push(`실패/중단된 학습이 ${failed}건 있습니다.`);
  return (
    <FindingsBody
      autoText={lines.join("\n")}
      editedText={editedText}
      onEditedTextChange={onEditedTextChange}
      editing={editing}
      onEditEnd={onEditEnd}
    />
  );
}

// 사용자가 수정한 소견이 있으면 그것을, 없으면 자동 소견을 표시. editing 이면 전체 내용을 편집
function FindingsBody({
  autoText,
  editedText = "",
  onEditedTextChange,
  editing,
  onEditEnd,
}: { autoText: string } & Pick<ContentProps, "editedText" | "onEditedTextChange" | "editing" | "onEditEnd">) {
  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (editing) setDraft(editedText || autoText);
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  if (editing) {
    const finish = (value: string | null) => {
      // 자동 소견과 같으면 "" 저장 → 이후 데이터 변경 시 자동 소견을 계속 따라감
      if (value !== null) onEditedTextChange?.(value.trim() === autoText.trim() ? "" : value);
      onEditEnd?.();
    };
    const btn = {
      fontSize: 12,
      padding: "4px 10px",
      borderRadius: 6,
      border: "1px solid #e5e7eb",
      background: "#fff",
      cursor: "pointer",
    };
    return (
      <Box>
        <TextField
          multiline
          minRows={4}
          fullWidth
          autoFocus
          size="small"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          helperText="한 줄에 소견 하나씩 입력"
          sx={{ "& textarea": { fontSize: 14, color: "var(--text-color)" } }}
        />
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
          <button style={btn} onClick={() => setDraft(autoText)}>
            자동 소견으로 되돌리기
          </button>
          <button style={btn} onClick={() => finish(null)}>
            취소
          </button>
          <button
            style={{
              ...btn,
              background: "#3b82f6",
              borderColor: "#3b82f6",
              color: "#fff",
            }}
            onClick={() => finish(draft)}
          >
            완료
          </button>
        </Box>
      </Box>
    );
  }

  const lines = (editedText || autoText).split("\n").filter((l) => l.trim());
  return (
    <Box>
      {editedText && (
        <Typography sx={{ fontSize: 11, color: "var(--text-secondary-color)", mb: 0.5 }}>✎ 사용자 수정됨</Typography>
      )}
      {lines.length ? (
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.5,
            color: "var(--text-color)",
            "& li": { mb: 0.75, fontSize: 14 },
          }}
        >
          {lines.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </Box>
      ) : (
        <Empty />
      )}
    </Box>
  );
}

// 클래스별 이미지 수: 라벨 vs AI 판정
function ClassImageCount({ m, colors }: ContentProps) {
  if (!m.classStats.length) return <Empty />;
  const data = m.classStats.map((s) => ({
    name: s.className,
    "사용자 라벨": s.labeled,
    "AI 판정": s.predicted,
  }));
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} barGap={2}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="name" tick={AXIS} />
        <YAxis tick={AXIS} allowDecimals={false} />
        <Tooltip />
        <Legend />
        <Bar dataKey="사용자 라벨" fill={series(colors, 0)} radius={[4, 4, 0, 0]} />
        <Bar dataKey="AI 판정" fill={series(colors, 1)} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// 클래스별 성능 표 (정확도 낮은 순)
function ClassPerformance({ m, colors }: ContentProps) {
  if (!m.classStats.length) return <Empty />;
  const rows = [...m.classStats].sort((a, b) => (a.recall ?? 2) - (b.recall ?? 2));
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {["클래스", "라벨", "AI 판정", "정답", "정확도", "정밀도", "과검(OK→NG)", "미검(NG→OK)"].map((h) => (
            <TableCell key={h} sx={headSx} align={h === "클래스" ? "left" : "right"}>
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((s) => (
          <TableRow key={s.className}>
            <TableCell sx={cellSx}>
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  mr: 1,
                  bgcolor: classColor(m, colors, s.className),
                }}
              />
              {s.className}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {s.labeled}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {s.predicted}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {s.correct}
            </TableCell>
            <TableCell
              sx={{
                ...cellSx,
                fontWeight: 700,
                color: (s.recall ?? 1) < 0.8 ? WARN : cellSx.color,
              }}
              align="right"
            >
              {pct(s.recall)}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {pct(s.precision)}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {s.overkill}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {s.missing}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// 혼동 행렬: 행 = 사용자 라벨(정답), 열 = AI 판정
function ConfusionMatrix({ m }: ContentProps) {
  const { labels, matrix } = m.confusion;
  const max = Math.max(1, ...matrix.flat());
  if (!labels.length || max === 1) return <Empty />;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table
        size="small"
        sx={{
          "& td, & th": { border: "1px solid var(--border-color, #e0e0e0)" },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>정답 \ AI</TableCell>
            {labels.map((l) => (
              <TableCell key={l} sx={headSx} align="center">
                {l}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {labels.map((gt, i) => (
            <TableRow key={gt}>
              <TableCell sx={headSx}>{gt}</TableCell>
              {matrix[i].map((v, j) => {
                const a = v / max;
                const diag = i === j;
                // 대각선 = 정답(파랑), 그 외 = 오판정(빨강) 단일 색상 농도
                const bg = v
                  ? diag
                    ? `rgba(42,120,214,${0.12 + a * 0.75})`
                    : `rgba(208,59,59,${0.12 + a * 0.75})`
                  : "transparent";
                return (
                  <TableCell
                    key={j}
                    align="center"
                    title={`정답 ${gt} → AI ${labels[j]}: ${v}장`}
                    sx={{
                      ...cellSx,
                      bgcolor: bg,
                      color: a > 0.55 ? "#fff" : cellSx.color,
                      fontWeight: diag ? 700 : 400,
                    }}
                  >
                    {v || ""}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// 공정별 불량 분포 + 오판정률
function ProcessDefect({ m, colors }: ContentProps) {
  // ponytail: 필터 상태는 화면 로컬(저장 안 됨). 리포트 저장 시 유지가 필요하면 ReportItem.filter 로 연결
  const [hideNoMismatch, setHideNoMismatch] = useState(false);
  if (!m.processStats.length) return <Empty />;
  const stats = hideNoMismatch ? m.processStats.filter((p) => p.mismatch > 0) : m.processStats;
  const data = stats.map((p) => ({ name: p.process, ...p.byClass }));
  const worst = [...stats].sort((a, b) => (b.mismatchRate ?? 0) - (a.mismatchRate ?? 0))[0];
  const hiddenCount = m.processStats.length - stats.length;
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
        }}
      >
        {hideNoMismatch && hiddenCount > 0 && (
          <Typography sx={{ fontSize: 12, color: "var(--text-secondary-color)" }}>{hiddenCount}개 숨김</Typography>
        )}
        <FormControlLabel
          control={
            <Switch size="small" checked={hideNoMismatch} onChange={(e) => setHideNoMismatch(e.target.checked)} />
          }
          label={<Typography sx={{ fontSize: 13, color: "var(--text-color)" }}>오판정 0건 숨기기</Typography>}
        />
      </Box>
      {!stats.length ? (
        <Empty>오판정이 있는 항목이 없습니다.</Empty>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="name" tick={AXIS} />
              <YAxis tick={AXIS} allowDecimals={false} />
              <Tooltip />
              <Legend />
              {m.classes.map((c) => (
                <Bar
                  key={c}
                  dataKey={c}
                  stackId="ng"
                  fill={classColor(m, colors, c)}
                  stroke="var(--report-card-bg, #fff)"
                  strokeWidth={1}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                {["공정", "이미지", "라벨링", "NG 라벨", "오판정", "오판정률", "과검"].map((h) => (
                  <TableCell key={h} sx={headSx} align={h === "공정" ? "left" : "right"}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.map((p) => {
                const isWorst = p === worst && (p.mismatchRate ?? 0) > 0;
                return (
                  <TableRow key={p.process}>
                    <TableCell sx={{ ...cellSx, fontWeight: isWorst ? 700 : 400 }}>
                      {p.process}
                      {isWorst && <span style={{ color: WARN, marginLeft: 6, fontSize: 12 }}>⚠ 주의</span>}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {p.total}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {p.labeled}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {Object.values(p.byClass).reduce((s, v) => s + v, 0)}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {p.mismatch}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellSx,
                        color: isWorst ? WARN : cellSx.color,
                        fontWeight: isWorst ? 700 : 400,
                      }}
                      align="right"
                    >
                      {pct(p.mismatchRate)}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {p.overkill}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
}

// 회차별 과검/미검 추이
function RoundPerformance({ m, colors }: ContentProps) {
  const data = m.rounds
    .filter((r) => r.overkill !== null)
    .map((r) => ({
      name: `${r.iteration}회`,
      과검률: +(r.overkill! * 100).toFixed(2),
      미검률: +(r.missing! * 100).toFixed(2),
    }));
  if (!data.length) return <Empty>기간 내 완료된 학습 결과가 없습니다.</Empty>;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="name" tick={AXIS} />
        <YAxis tick={AXIS} unit="%" />
        <Tooltip formatter={(v: number) => `${v}%`} />
        <Legend />
        <Line dataKey="과검률" stroke={series(colors, 1)} strokeWidth={2} dot={{ r: 4 }} />
        <Line dataKey="미검률" stroke={series(colors, 0)} strokeWidth={2} dot={{ r: 4 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// 학습 이력 표
function TrainHistory({ m }: ContentProps) {
  if (!m.rounds.length) return <Empty>기간 내 학습 이력이 없습니다.</Empty>;
  const fmtDur = (s: number | null) => (s === null ? "-" : s >= 60 ? `${Math.round(s / 60)}분` : `${Math.round(s)}초`);
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {["회차", "일시", "Epoch", "학습 이미지", "소요", "상태", "과검률", "미검률", "임계값"].map((h) => (
            <TableCell key={h} sx={headSx} align={h === "회차" || h === "일시" ? "left" : "right"}>
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {[...m.rounds].reverse().map((r) => (
          <TableRow key={r.iteration}>
            <TableCell sx={cellSx}>{r.iteration}</TableCell>
            <TableCell sx={cellSx}>{r.create_at.slice(0, 16).replace("T", " ")}</TableCell>
            <TableCell sx={cellSx} align="right">
              {r.epoch}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {r.imageCount}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {fmtDur(r.durationSec)}
            </TableCell>
            <TableCell
              sx={{
                ...cellSx,
                color: r.status === "Finished" ? cellSx.color : WARN,
              }}
              align="right"
            >
              {r.status}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {pct(r.overkill, 2)}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {pct(r.missing, 2)}
            </TableCell>
            <TableCell sx={cellSx} align="right">
              {r.threshold ?? "-"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const fmtDate = (s?: string) => (s ? s.slice(0, 16).replace("T", " ") : "-");

// 검사 리포트 상단: 사용된 검증 결과·모델 표기 (validHistories + onChange 주면 선택 가능)
export function ValidationInfo({
  m,
  validHistories,
  onChange,
}: {
  m: ReportMetrics;
  validHistories?: { id: number; finished_at: string }[];
  onChange?: (id: number) => void;
}) {
  if (!m.isInspection) return null;
  const v = m.validation;
  const item = (label: string, value: ReactNode) => (
    <Box>
      <Typography sx={{ fontSize: 12, color: "var(--text-secondary-color)" }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--text-color)" }}>{value}</Typography>
    </Box>
  );
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 3,
        alignItems: "center",
        p: 1.5,
        mb: 2,
        borderRadius: 2,
        border: "1px solid var(--border-color, #e0e0e0)",
      }}
    >
      {!v ? (
        <Typography sx={{ color: WARN, fontSize: 14 }}>
          완료된 검증 결과가 없습니다. 검증 후 리포트가 구성됩니다.
        </Typography>
      ) : (
        <>
          {item(
            "검증 결과",
            validHistories && onChange ? (
              <select
                value={v.id}
                onChange={(e) => onChange(Number(e.target.value))}
                style={{
                  font: "inherit",
                  color: "inherit",
                  background: "transparent",
                  border: "1px solid var(--border-color, #ccc)",
                  borderRadius: 4,
                  padding: "2px 4px",
                }}
              >
                {validHistories.map((h) => (
                  <option key={h.id} value={h.id}>
                    #{h.id} · {fmtDate(h.finished_at)}
                  </option>
                ))}
              </select>
            ) : (
              `#${v.id} · ${fmtDate(v.finishedAt)}`
            ),
          )}
          {item("사용 모델", v.modelName)}
          {item("모델 UUID", <span style={{ fontWeight: 400, fontSize: 12 }}>{v.modelUuid}</span>)}
          {item("모델 생성일", fmtDate(v.modelCreateAt))}
          {item("임계값", v.threshold?.toFixed(4) ?? "-")}
          {item("검증 이미지", `${m.total.toLocaleString()}장`)}
        </>
      )}
    </Box>
  );
}

export const EDITED_TEXT_FILTER = "findingsText"; // ReportItem.filter 에 저장되는 type

export const REPORT_CONTENTS: Record<
  string,
  {
    title: string;
    gridSize: 4 | 6 | 8 | 12;
    Component: (p: ContentProps) => JSX.Element;
    editable?: boolean; // 카드 헤더에 연필(편집) 아이콘 표시
  }
> = {
  summary: { title: "요약", gridSize: 12, Component: Summary },
  findings: {
    title: "분석 소견",
    gridSize: 12,
    Component: Findings,
    editable: true,
  },
  classImageCount: {
    title: "클래스별 이미지 수",
    gridSize: 6,
    Component: ClassImageCount,
  },
  classPerformance: {
    title: "클래스별 판정 성능",
    gridSize: 6,
    Component: ClassPerformance,
  },
  confusionMatrix: {
    title: "혼동 행렬 (정답 × AI)",
    gridSize: 6,
    Component: ConfusionMatrix,
  },
  roundPerformance: {
    title: "회차별 과검/미검률",
    gridSize: 6,
    Component: RoundPerformance,
  },
  processDefect: {
    title: "공정별 불량 분포",
    gridSize: 12,
    Component: ProcessDefect,
  },
  trainHistory: { title: "학습 이력", gridSize: 12, Component: TrainHistory },
};

// 템플릿/샘플 공용 기본 구성
export const DEFAULT_LAYOUT = Object.entries(REPORT_CONTENTS).map(([type, c], i) => ({
  index: i + 1,
  type,
  gridSize: c.gridSize,
}));
